import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  /** What failed, in the message: "Stock", "Farms & Sheds". */
  label: string;
  children: ReactNode;
  /** Shown as a second button, e.g. going back to the dashboard. */
  onLeave?: () => void;
  leaveLabel?: string;
}

interface State {
  error: Error | null;
}

/**
 * Keeps one broken screen from blanking the whole app. The sidebar, top bar
 * and every other screen keep working; the broken one offers to try again.
 *
 * App keys it by screen, so moving to another screen starts fresh.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Left in the console for whoever is asked to look at it.
    console.error(`${this.props.label} stopped working`, error, info.componentStack);
  }

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <section className="panel crashpanel" role="alert">
        <h2>This screen stopped working</h2>
        <p>
          Something went wrong in <strong>{this.props.label}</strong>. The rest of the app still works. Try
          again, and if it keeps happening, tell your administrator what you were doing.
        </p>
        <details className="crashpanel__detail">
          <summary>Details for the administrator</summary>
          <pre>{error.message}</pre>
        </details>
        <div className="formactions">
          <button type="button" className="btn btn--primary" onClick={() => this.setState({ error: null })}>
            Try again
          </button>
          {this.props.onLeave && (
            <button type="button" className="btn" onClick={this.props.onLeave}>
              {this.props.leaveLabel ?? 'Go back'}
            </button>
          )}
        </div>
      </section>
    );
  }
}
